<template>
  <div class="in-app-ads">

    <!-- Create Ad Dialog -->
    <v-dialog max-width="600" v-model="createAdDialog">
      <v-card class="pa-10">
        <v-form @submit.prevent="create">
          <v-row align-content="end">
            <v-col cols="12">
              <v-file-input
                v-model="newFile"
                outlined
                ref="fileupload"
                label="Upload Image"
                hint="Image should be exactly 1200x628 pixels."
                @change="onFileChange"
                :loading="uploadLoading"
              />
            </v-col>

            <v-col cols="12" v-if="uploadedFile">
              <v-responsive>
                <v-img :src="uploadedFile.fileUrl" />
              </v-responsive>
            </v-col>

            <v-col cols="12">
              <v-text-field
                outlined
                v-model="newRedirectUrl"
                label="Redirect Url"
                hint="Make sure to include https://"
              />
            </v-col>

            <v-col cols="6">
              <v-text-field
                type="datetime-local"
                outlined
                v-model="newEnableAt"
                label="Enable At"
                hint="Leave blank to enable immediately"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                type="datetime-local"
                outlined
                v-model="newDisableAt"
                label="Disable At"
                hint="When this ad should stop showing"
              />
            </v-col>

            <v-col cols="12">
              <v-btn
                color="primary"
                :disabled="!canSubmit"
                block
                depressed
                type="submit">
                Create
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Edit Ad Dialog -->
    <v-dialog max-width="600" v-model="editAdDialog">
      <v-card class="pa-10">
        <v-form @submit.prevent="saveEdit">
          <v-row align-content="end">
            <v-col cols="12">
              <v-responsive>
                <v-img v-if="editItem && editItem.file_upload" :src="editItem.file_upload.fileUrl" style="max-height: 200px;" contain />
                <v-img v-if="editItem && !editItem.file_upload && editItem.image_url" :src="editItem.image_url" style="max-height: 200px;" contain />
              </v-responsive>
            </v-col>

            <v-col cols="12">
              <v-text-field
                outlined
                v-model="editRedirectUrl"
                label="Redirect Url"
                hint="Make sure to include https://"
              />
            </v-col>

            <v-col cols="6">
              <v-text-field
                type="datetime-local"
                outlined
                v-model="editEnableAt"
                label="Enable At"
                hint="Leave blank to enable immediately"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                type="datetime-local"
                outlined
                v-model="editDisableAt"
                label="Disable At"
                hint="When this ad should stop showing"
              />
            </v-col>

            <v-col cols="12">
              <v-btn
                color="primary"
                block
                depressed
                type="submit">
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Actions -->
    <v-row justify="end">
      <v-col cols="4">
        <v-btn block depressed color="primary" @click="openCreateDialog">
          Create Ad
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabbed table -->
    <v-row v-if="!marianaInAppAdsLoading">
      <v-col cols="12">
        <v-tabs v-model="activeTab" class="mb-4">
          <v-tab>Active</v-tab>
          <v-tab>Inactive</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <!-- Active Tab -->
          <v-window-item>
            <v-simple-table v-if="activeAds.length">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Active</th>
                    <th class="text-left">Image</th>
                    <th class="text-left">Redirect</th>
                    <th class="text-left">Enable At</th>
                    <th class="text-left">Disable At</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in activeAds" :key="item.id">
                    <td><v-simple-checkbox :value="item.active" @click.prevent="update(item)"></v-simple-checkbox></td>
                    <td v-if="!item.file_id"><v-responsive><img class="pt-3 pb-1" style="max-width: 100px" :src="item.image_url" /></v-responsive></td>
                    <td v-else><v-responsive><img class="pt-3 pb-1" style="max-width: 100px" :src="item.file_upload.fileUrl" /></v-responsive></td>
                    <td><a :href="item.redirect_url" target="_blank">{{ item.redirect_url }}</a></td>
                    <td>{{ formatDateTime(item.enableAt) }}</td>
                    <td>{{ formatDateTime(item.disabledAt) }}</td>
                    <td>
                      <v-btn icon depressed @click.prevent="openEditDialog(item)">
                        <v-icon>mdi_edit</v-icon>
                      </v-btn>
                      <v-btn icon depressed @click.prevent="remove(item.id)">
                        <v-icon>mdi_delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <v-alert v-else type="info" text="No active ads." class="mt-4" outlined>No active ads.</v-alert>
          </v-window-item>

          <!-- Inactive Tab -->
          <v-window-item>
            <v-simple-table v-if="inactiveAds.length">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Active</th>
                    <th class="text-left">Image</th>
                    <th class="text-left">Redirect</th>
                    <th class="text-left">Enable At</th>
                    <th class="text-left">Disable At</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in inactiveAds" :key="item.id">
                    <td><v-simple-checkbox :value="item.active" @click.prevent="update(item)"></v-simple-checkbox></td>
                    <td v-if="!item.file_id"><v-responsive><img class="pt-3 pb-1" style="max-width: 100px" :src="item.image_url" /></v-responsive></td>
                    <td v-else><v-responsive><img class="pt-3 pb-1" style="max-width: 100px" :src="item.file_upload.fileUrl" /></v-responsive></td>
                    <td><a :href="item.redirect_url" target="_blank">{{ item.redirect_url }}</a></td>
                    <td>{{ formatDateTime(item.enableAt) }}</td>
                    <td>{{ formatDateTime(item.disabledAt) }}</td>
                    <td>
                      <v-btn icon depressed @click.prevent="openEditDialog(item)">
                        <v-icon>mdi_edit</v-icon>
                      </v-btn>
                      <v-btn icon depressed @click.prevent="remove(item.id)">
                        <v-icon>mdi_delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <v-alert v-else type="info" text="No inactive ads." class="mt-4" outlined>No inactive ads.</v-alert>
          </v-window-item>
        </v-window>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import { DateTime } from 'luxon'

export default {
  name: 'InAppAdForm',
  data() {
    return {
      image_url: null,
      newRedirectUrl: null,
      newFile: null,
      newEnableAt: null,
      newDisableAt: null,
      file: null,
      createAdDialog: false,
      editAdDialog: false,
      uploadedFile: null,
      formData: null,
      uploadLoading: false,
      editItem: null,
      editRedirectUrl: null,
      editEnableAt: null,
      editDisableAt: null,
      activeTab: 'active',
    }
  },
  computed: {
    ...mapGetters({
      marianaInAppAds: 'mariana-in-app-ad/list',
    }),
    marianaInAppAdsLoading() {
      return this.$store.state['mariana-in-app-ad'].isFindPending
    },
    canSubmit() {
      return this.file !== null && this.newRedirectUrl !== null
    },
    activeAds() {
      const now = DateTime.now().toISO()
      return this.marianaInAppAds.filter(item => {
        const isActive = item.active === true
        const inScheduleWindow = (!item.enableAt || now >= item.enableAt) && (!item.disabledAt || now <= item.disabledAt)
        // Active: active flag is true AND current datetime falls within the enableAt/disableAt window
        return isActive && inScheduleWindow
      })
    },
    inactiveAds() {
      const now = DateTime.now().toISO()
      return this.marianaInAppAds.filter(item => {
        const isActive = item.active === true
        const inScheduleWindow = (!item.enableAt || now >= item.enableAt) && (!item.disabledAt || now <= item.disabledAt)
        // Inactive: active flag is false OR current datetime falls outside the enableAt/disableAt window
        return !(isActive && inScheduleWindow)
      })
    },
  },
  methods: {
    formatDateTime(value) {
      if (!value) return '—'
      return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED)
    },
    openCreateDialog() {
      this.newRedirectUrl = null
      this.newFile = null
      this.newEnableAt = null
      this.newDisableAt = null
      this.file = null
      this.uploadedFile = null
      this.createAdDialog = true
    },
    openEditDialog(item) {
      this.editItem = item
      this.editRedirectUrl = item.redirect_url || ''
      // Convert timestamp to datetime-local format (YYYY-MM-DDTHH:MM)
      if (item.enableAt) {
        this.editEnableAt = this.timestampToDateTimeLocal(item.enableAt)
      } else {
        this.editEnableAt = null
      }
      if (item.disabledAt) {
        this.editDisableAt = this.timestampToDateTimeLocal(item.disabledAt)
      } else {
        this.editDisableAt = null
      }
      this.editAdDialog = true
    },
    timestampToDateTimeLocal(timestamp) {
      return DateTime.fromISO(timestamp).toFormat("yyyy-MM-dd'T'HH:mm")
    },
    async onFileChange(file) {
      this.file = file
      await this.uploadImage()
    },
    async uploadImage() {
      if (this.file) {
        this.formData = new FormData()
        this.formData.append('file', this.file)
        this.formData.append('eccoType', 'in-app-ad')
        this.uploadLoading = true
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/file-upload`, this.formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${this.$store.state.auth.accessToken}` }
        })
        console.log(response)
        this.uploadedFile = response.data
        this.uploadLoading = false
      }
    },
    async create() {
      await this.$store.dispatch('mariana-in-app-ad/create', {
        file_id: this.uploadedFile.id,
        redirect_url: this.newRedirectUrl,
        active: true,
        enableAt: this.newEnableAt ? DateTime.fromISO(this.newEnableAt).toISO() : DateTime.now().toISO(),
        disabledAt: this.newDisableAt ? DateTime.fromISO(this.newDisableAt).toISO() : null,
      })

      // Clear form
      this.newRedirectUrl = null
      this.newFile = null
      this.newEnableAt = null
      this.newDisableAt = null
      this.file = null
      this.uploadedFile = null
      this.createAdDialog = false
      this.formData = null
      await this.fetchInAppAds()
    },
    async saveEdit() {
      if (!this.editItem) return
      await this.$store.dispatch('mariana-in-app-ad/patch', [this.editItem.id, {
        redirect_url: this.editRedirectUrl,
        enableAt: this.editEnableAt ? DateTime.fromISO(this.editEnableAt).toISO() : DateTime.now().toISO(),
        disabledAt: this.editDisableAt ? DateTime.fromISO(this.editDisableAt).toISO() : null,
      }])
      this.editAdDialog = false
      this.editItem = null
      await this.fetchInAppAds()
    },
    async update(item) {
      await this.$store.dispatch('mariana-in-app-ad/patch', [item.id, {
        active: !item.active,
      }])
    },
    async remove(id) {
      await this.$store.dispatch('mariana-in-app-ad/remove', [id])
      await this.fetchInAppAds()
    },
    async fetchInAppAds() {
      await this.$store.dispatch('mariana-in-app-ad/find', {
        query: {
          $limit: 50,
        }
      })
    },
  },
  async mounted() {
    await this.fetchInAppAds()
  }
}
</script>

<style lang="sass">
</style>
